"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_BASE_MOTION_TOKENS = exports.APP_BASE_MOTION_ATTRIBUTE = void 0;
exports.installAppBaseMotion = installAppBaseMotion;
/** `data-sl-motion="off"` on an element or an ancestor shows its changes without motion. */
exports.APP_BASE_MOTION_ATTRIBUTE = 'data-sl-motion';
/**
 * The motion tokens an App or a theme may set on `:root`; the values here are
 * the ones of StreamLain. A duration is read in `ms` or `s`.
 */
exports.APP_BASE_MOTION_TOKENS = {
    '--sl-motion-duration-fast': '160ms',
    '--sl-motion-duration-base': '240ms',
    '--sl-motion-duration-slow': '360ms',
    '--sl-motion-stagger': '24ms',
    '--sl-motion-distance': '6px',
    '--sl-motion-ease-standard': 'cubic-bezier(0.2, 0, 0, 1)',
    '--sl-motion-ease-enter': 'cubic-bezier(0.16, 1, 0.3, 1)',
    '--sl-motion-ease-exit': 'cubic-bezier(0.4, 0, 1, 1)',
    '--sl-motion-ease-in-out': 'cubic-bezier(0.42, 0, 0.58, 1)',
    '--sl-motion-ease-spring': 'cubic-bezier(0.34, 1.56, 0.64, 1)',
};
/**
 * Animates every state change of an App page: what appears comes in, what
 * goes fades out, a row that changes place glides there and its neighbours
 * follow, a changed short value pops, a `<details>` opens and closes, a
 * pressed button gives way while it is held, and a new page comes in after a
 * page change.
 * A row is known by its table row or by `data-sl-key`, so a page that draws
 * its rows anew still moves them. A visitor who asked for less motion sees
 * every change at once. A part the App moves itself keeps its own motion:
 * this one leaves it, what it holds and what holds it alone, also when the
 * App starts moving it while this one plays. Embedded as a function, so it uses nothing outside
 * its own body: the bridges hand it `APP_BASE_MOTION_TOKENS` as `defaults`.
 */
function installAppBaseMotion(window, defaults) {
    const document = window.document;
    // An App Container brings the bridge once per App; the window has one motion.
    const installed = '__streamlainAppBaseMotion';
    const host = window;
    if (host[installed])
        return host[installed];
    // A window without layout (a server, a test) has nothing to move.
    if (typeof window.Element?.prototype.animate !== 'function' ||
        typeof window.MutationObserver !== 'function')
        return { dispose() { } };
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const ghostAttribute = 'data-sl-motion-ghost';
    const offSelector = '[data-sl-motion="off"]';
    const keyedSelector = '[data-sl-key], [data-sl-binding-row]';
    const popLimit = 24;
    const scrollSettleMs = 120;
    const staggerLimitMs = 160;
    /** The tokens as set on `:root`, read once per change. */
    const tokens = () => {
        const style = window.getComputedStyle(document.documentElement);
        const token = (name) => style.getPropertyValue(name).trim() || defaults[name];
        const ms = (name) => {
            const value = token(name);
            const number = Number.parseFloat(value);
            if (!Number.isFinite(number))
                return Number.parseFloat(defaults[name]);
            return /\ds$/.test(value) && !value.endsWith('ms')
                ? number * 1000
                : number;
        };
        return {
            fast: ms('--sl-motion-duration-fast'),
            base: ms('--sl-motion-duration-base'),
            slow: ms('--sl-motion-duration-slow'),
            stagger: ms('--sl-motion-stagger'),
            distance: token('--sl-motion-distance'),
            /** The distance in pixels, as far as a shift of a row needs it. */
            distancePx: (() => {
                const value = token('--sl-motion-distance');
                const number = Number.parseFloat(value);
                if (!Number.isFinite(number))
                    return 0;
                if (value.endsWith('rem'))
                    return number * (Number.parseFloat(style.fontSize) || 16);
                return value.endsWith('px') ? number : 0;
            })(),
            standard: token('--sl-motion-ease-standard'),
            enter: token('--sl-motion-ease-enter'),
            exit: token('--sl-motion-ease-exit'),
            inOut: token('--sl-motion-ease-in-out'),
            spring: token('--sl-motion-ease-spring'),
        };
    };
    /** Identity of a row: its own key, or the list it belongs to plus its table row. */
    const keyOf = (element) => {
        const own = element.getAttribute('data-sl-key');
        if (own !== null)
            return `key:${own}`;
        const row = element.getAttribute('data-sl-binding-row');
        if (row === null)
            return null;
        return `row:${element.getAttribute('data-sl-binding-owner') ?? ''}:${row}`;
    };
    const still = (element) => reduced?.matches === true ||
        element.closest(offSelector) !== null ||
        element.closest(`[${ghostAttribute}]`) !== null;
    /** A part nobody sees, in a hidden tab or a closed panel, does not move. */
    const unseen = (element) => element.closest('[hidden]') !== null ||
        (typeof element.checkVisibility === 'function' &&
            !element.checkVisibility());
    /**
     * Properties whose motion moves a part. An animation of one of them that
     * the App plays itself goes first; a loop that runs on, such as a spinner,
     * and a change of colour leave this motion be.
     */
    const movingProperty = /^(transform|translate|scale|rotate|opacity|top|right|bottom|left|inset|width|height|margin)/i;
    /** What this motion plays: every other animation is the App's own. */
    const ownAnimations = new WeakSet();
    const playing = new Set();
    const isAppMotion = (animation) => {
        if (ownAnimations.has(animation) || animation.playState !== 'running')
            return false;
        const effect = animation.effect;
        if (!effect || effect.getTiming().iterations === Infinity)
            return false;
        if ('transitionProperty' in animation)
            return movingProperty.test(String(animation.transitionProperty));
        const frames = typeof effect.getKeyframes === 'function'
            ? effect.getKeyframes()
            : [];
        return frames.some(frame => Object.keys(frame).some(name => movingProperty.test(name)));
    };
    /**
     * Parts the App was seen moving itself. Their motion is the App's: when
     * the App takes one out after its own motion, no copy of it fades out.
     */
    const appMoved = new WeakSet();
    /** The animations the App plays itself right now. */
    const appMotions = () => typeof document.getAnimations === 'function'
        ? document.getAnimations().filter(isAppMotion)
        : [];
    /** The parts the App moves itself right now. */
    const appMoving = () => {
        const targets = [];
        for (const animation of appMotions()) {
            const target = animation.effect?.target;
            if (!target)
                continue;
            targets.push(target);
            appMoved.add(target);
        }
        return targets;
    };
    /** Whether a part is, holds or lies in one the App moves. */
    const touches = (element, targets) => targets.some(target => target === element ||
        target.contains(element) ||
        element.contains(target));
    /**
     * While this motion plays, an App motion that starts on the same part
     * takes over: this one stops at once, so the part shows where it is.
     */
    let watchFrame = 0;
    /**
     * The animations this motion started for the last change, and when: an
     * App motion that starts within the frames after it makes the change the
     * App's, so this motion takes back all it played for it.
     */
    let lastChange = null;
    let recording = null;
    const changeFrames = 3;
    const timelineNow = () => Number(document.timeline?.currentTime ?? 0);
    /** Whether the App starts a motion of its own in the change at hand. */
    const appStarting = () => appMotions().some(animation => animation.pending || animation.startTime === null);
    const appStartedSince = (at) => appMotions().some(animation => animation.pending ||
        animation.startTime === null ||
        Number(animation.startTime) >= at);
    const yieldToApp = () => {
        if (lastChange) {
            if (timelineNow() - lastChange.at > changeFrames * 17)
                lastChange = null;
            else if (appStartedSince(lastChange.at)) {
                for (const animation of lastChange.animations) {
                    playing.delete(animation);
                    animation.cancel();
                }
                lastChange = null;
            }
        }
        if (playing.size === 0)
            return;
        const targets = appMoving();
        if (targets.length === 0)
            return;
        for (const animation of Array.from(playing)) {
            const target = animation.effect?.target;
            if (!target || !touches(target, targets))
                continue;
            playing.delete(animation);
            animation.cancel();
        }
    };
    const watch = () => {
        watchFrame = 0;
        yieldToApp();
        if (playing.size > 0)
            watchFrame = window.requestAnimationFrame(watch);
    };
    /** Plays an animation of this motion and keeps it known as its own. */
    const play = (element, frames, options) => {
        const animation = element.animate(frames, options);
        ownAnimations.add(animation);
        playing.add(animation);
        recording?.push(animation);
        const done = () => {
            playing.delete(animation);
        };
        void animation.finished?.then(done, done);
        if (!watchFrame)
            watchFrame = window.requestAnimationFrame(watch);
        return animation;
    };
    /** Controls whose text is their label, not a value of the page. */
    const controlSelector = 'button, a, summary, label, [role="button"], [role="tab"], [role="link"]';
    const shifts = new WeakMap();
    const shiftOf = (element) => {
        const shift = shifts.get(element);
        const progress = shift?.animation.effect?.getComputedTiming().progress;
        if (!shift ||
            typeof progress !== 'number' ||
            shift.animation.playState === 'finished' ||
            shift.animation.playState === 'idle') {
            shifts.delete(element);
            return { x: 0, y: 0 };
        }
        return { x: shift.x * (1 - progress), y: shift.y * (1 - progress) };
    };
    /** Plays a shift from `x`/`y` to the place of an element and keeps it known. */
    const shift = (element, kind, frames, options, from) => {
        const animation = play(element, frames, options);
        shifts.set(element, {
            animation,
            x: from.x,
            y: from.y,
            kind,
            frames,
            options,
        });
        return animation;
    };
    /** What all shifts of an element and the parts around it move it by now. */
    const shiftWithin = (element) => {
        let x = 0;
        let y = 0;
        for (let node = element; node; node = node.parentElement) {
            const shift = shiftOf(node);
            x += shift.x;
            y += shift.y;
        }
        return { x, y };
    };
    /**
     * How far the scroll boxes around an element move it now, known for one
     * reading of the layout. A box plus this is its place as laid out, so a
     * scroll alone moves most rows by nothing; what it does move (a sticky or
     * an absolutely placed part) is read anew once the scroll ends. A fixed
     * part does not scroll with what is around it.
     */
    let scrolledBy = new Map();
    const scrolledOf = (element) => {
        const known = scrolledBy.get(element);
        if (known)
            return known;
        const parent = element.parentElement;
        // Only a fixed part, a part not laid out and the root have no offset
        // parent; the style tells the fixed one apart.
        const fixed = element instanceof window.HTMLElement &&
            element.offsetParent === null &&
            element !== document.body &&
            element !== document.documentElement &&
            window.getComputedStyle(element).position === 'fixed';
        let scrolled = { x: 0, y: 0 };
        if (parent && !fixed) {
            const outer = scrolledOf(parent);
            scrolled = {
                x: outer.x + parent.scrollLeft,
                y: outer.y + parent.scrollTop,
            };
        }
        scrolledBy.set(element, scrolled);
        return scrolled;
    };
    /** Place of a row as laid out, without the shifts playing on it. */
    const placeOf = (element) => {
        const box = element.getBoundingClientRect();
        if (box.width === 0 && box.height === 0)
            return null;
        const shift = shiftWithin(element);
        const scrolled = scrolledOf(element);
        return {
            x: box.left + scrolled.x - shift.x,
            y: box.top + scrolled.y - shift.y,
        };
    };
    /** Place of every row as laid out, and the row shown there, as of the last change. */
    let places = new Map();
    let placed = new Map();
    const measure = () => {
        scrolledBy = new Map();
        const next = new Map();
        const shown = new Map();
        for (const element of Array.from(document.querySelectorAll(keyedSelector))) {
            const key = keyOf(element);
            if (key === null || next.has(key))
                continue;
            const place = placeOf(element);
            if (!place)
                continue;
            next.set(key, place);
            shown.set(key, element);
        }
        places = next;
        placed = shown;
    };
    let measureFrame = 0;
    const measureSoon = () => {
        if (measureFrame)
            return;
        measureFrame = window.requestAnimationFrame(() => {
            measureFrame = 0;
            measure();
        });
    };
    /** Parts a copy would load or play again. */
    const loadingSelector = 'iframe, frame, embed, object, video, audio';
    /**
     * What one change fades out at most: more parts, or larger ones, go at once,
     * since copying their looks would cost more than it shows.
     */
    const ghostLimit = 24;
    const ghostNodeLimit = 240;
    /**
     * The layer copies fade out on: a closed shadow root beside the body, so
     * the page of the App holds only what the App wrote, and no query of it
     * finds a copy. Shown in the top layer, above an open dialog.
     */
    let layerHost = null;
    let layer = null;
    const layerOf = () => {
        if (!layer || !layerHost?.isConnected) {
            layerHost = document.createElement('div');
            layerHost.setAttribute('data-sl-motion-layer', '');
            layerHost.setAttribute('aria-hidden', 'true');
            layerHost.setAttribute('popover', 'manual');
            layerHost.style.cssText =
                'position:fixed;inset:0;width:auto;height:auto;margin:0;padding:0;' +
                    'border:0;background:transparent;overflow:visible;' +
                    'pointer-events:none;z-index:2147483647';
            layer = layerHost.attachShadow({ mode: 'closed' });
            document.documentElement.append(layerHost);
        }
        // Shown again, it lies above what opened since, such as a modal dialog.
        if (typeof layerHost.showPopover === 'function') {
            if (layerHost.matches(':popover-open'))
                layerHost.hidePopover();
            layerHost.showPopover();
        }
        return layer;
    };
    /** Overflow values that cut off what lies outside a box. */
    const clipping = /^(hidden|clip|scroll|auto)$/;
    /** The part of `box` that the boxes around `parent` that clip it leave visible. */
    const clipOf = (parent, box) => {
        let top = box.top;
        let left = box.left;
        let right = box.right;
        let bottom = box.bottom;
        for (let node = parent; node && node !== document.documentElement; node = node.parentElement) {
            const style = window.getComputedStyle(node);
            if (!clipping.test(style.overflowX) && !clipping.test(style.overflowY))
                continue;
            const outer = node.getBoundingClientRect();
            top = Math.max(top, outer.top);
            left = Math.max(left, outer.left);
            right = Math.min(right, outer.right);
            bottom = Math.min(bottom, outer.bottom);
        }
        if (top === box.top &&
            left === box.left &&
            right === box.right &&
            bottom === box.bottom)
            return null;
        return {
            top: top - box.top,
            right: box.right - right,
            bottom: box.bottom - bottom,
            left: left - box.left,
        };
    };
    /** Every style of a computed declaration, as one inline text. */
    const styleText = (computed) => Array.from(computed)
        .map(name => `${name}:${computed.getPropertyValue(name)};`)
        .join('');
    /**
     * The parts that went show a moment longer as copies that fade out where
     * they were, on the layer above the page: copies without ids and data
     * attributes, with empty frames and players, so they load and play nothing
     * again, and with the looks the parts had, read into their own styles,
     * since the styles of the page do not reach the layer. The copies of one
     * change are put back where the parts were, read in one go and taken out
     * again before any script of the page runs; an observer of the page sees
     * that as one change of nodes marked `data-sl-motion-ghost`. What followed
     * a part takes its place at once; rows with a key glide there.
     */
    const fadeOut = (gone, motion) => {
        let count = 0;
        let budget = ghostNodeLimit;
        const placed = [];
        for (const { parent, before, parts } of gone) {
            if (!parent.isConnected || unseen(parent))
                continue;
            const copies = document.createDocumentFragment();
            for (const part of parts) {
                if (count >= ghostLimit)
                    break;
                const copy = part.cloneNode(true);
                const nodes = [copy, ...Array.from(copy.querySelectorAll('*'))];
                if (nodes.length > budget)
                    continue;
                budget -= nodes.length;
                count += 1;
                for (const node of nodes) {
                    for (const name of node.getAttributeNames())
                        if (name === 'id' || name === 'name' || name.startsWith('data-'))
                            node.removeAttribute(name);
                    if (node.matches(loadingSelector)) {
                        for (const name of ['src', 'srcdoc', 'data', 'autoplay'])
                            node.removeAttribute(name);
                        node.replaceChildren();
                    }
                }
                copy.removeAttribute('hidden');
                copy.setAttribute(ghostAttribute, '');
                copies.append(copy);
                placed.push({ parent, copy, nodes });
            }
            parent.insertBefore(copies, before?.parentNode === parent ? before : null);
        }
        if (placed.length === 0)
            return;
        // One reading of the layout for all copies of this change.
        const read = placed.map(({ parent, copy, nodes }) => {
            const box = copy.getBoundingClientRect();
            return {
                copy,
                nodes,
                box,
                clip: clipOf(parent, box),
                looks: nodes.map(node => ({
                    own: styleText(window.getComputedStyle(node)),
                    marks: ['::before', '::after'].flatMap(mark => {
                        const computed = window.getComputedStyle(node, mark);
                        const content = computed.getPropertyValue('content');
                        return content && content !== 'none' && content !== 'normal'
                            ? [{ mark, text: styleText(computed) }]
                            : [];
                    }),
                })),
            };
        });
        for (const { copy } of placed)
            copy.remove();
        let target = null;
        for (const { copy, nodes, box, clip, looks } of read) {
            if (box.width === 0 && box.height === 0)
                continue;
            // A copy wholly out of the window is not seen fading.
            if (box.bottom < 0 ||
                box.right < 0 ||
                box.top > window.innerHeight ||
                box.left > window.innerWidth)
                continue;
            if (!(copy instanceof window.HTMLElement ||
                copy instanceof window.SVGElement))
                continue;
            const marks = [];
            nodes.forEach((node, index) => {
                const look = looks[index];
                if (node instanceof window.HTMLElement ||
                    node instanceof window.SVGElement)
                    // The looks of the part carry its pointer-events; a copy takes no
                    // click from the page that now lies beneath it.
                    node.style.cssText = `${look.own}animation:none;transition:none;pointer-events:none;`;
                if (look.marks.length === 0)
                    return;
                node.setAttribute('data-sl-motion-mark', String(index));
                for (const { mark, text } of look.marks)
                    marks.push(`[data-sl-motion-mark="${index}"]${mark}{${text}animation:none;transition:none}`);
            });
            copy.style.cssText +=
                `position:absolute;left:${box.left}px;top:${box.top}px;` +
                    `width:${box.width}px;height:${box.height}px;right:auto;bottom:auto;` +
                    'margin:0;box-sizing:border-box;transform:none;translate:none;' +
                    'scale:none;rotate:none;pointer-events:none;' +
                    (clip
                        ? `clip-path:inset(${clip.top}px ${clip.right}px ${clip.bottom}px ${clip.left}px);`
                        : '');
            const holder = document.createElement('div');
            if (marks.length > 0) {
                const sheet = document.createElement('style');
                sheet.textContent = marks.join('\n');
                holder.append(sheet);
            }
            holder.append(copy);
            target ??= layerOf();
            target.append(holder);
            void play(copy, [{ opacity: 1 }, { opacity: 0, transform: 'scale(0.98)' }], { duration: motion.fast, easing: motion.exit })
                .finished.catch(() => undefined)
                .then(() => holder.remove());
        }
    };
    /**
     * The node the parts a record took away stood before, as far as it is
     * still there: after the node they followed, or before the one they
     * preceded; at the start when they were first.
     */
    const placeOfGone = (record) => {
        const parent = record.target;
        const after = record.previousSibling;
        if (after === null)
            return parent.firstChild;
        if (after.parentNode === parent)
            return after.nextSibling;
        if (record.nextSibling?.parentNode === parent)
            return record.nextSibling;
        return null;
    };
    /** Between leaving a page and entering the next one only the page itself moves. */
    let changingPage = false;
    const settle = (records) => {
        records = records.filter(record => !(record.target instanceof window.Element &&
            record.target.closest(`[${ghostAttribute}]`)) &&
            !Array.from(record.addedNodes).some(node => node instanceof window.Element && node.hasAttribute(ghostAttribute)) &&
            !Array.from(record.removedNodes).some(node => node instanceof window.Element && node.hasAttribute(ghostAttribute)));
        if (changingPage || records.length === 0)
            return;
        const motion = tokens();
        // A change in which the App starts a motion of its own is the App's:
        // this motion plays nothing for it and only reads where the rows are.
        if (appStarting()) {
            measure();
            return;
        }
        // What the App moves itself as of this change keeps its own motion.
        const appMoves = appMoving();
        recording = [];
        try {
            playChange(records, motion, appMoves);
        }
        finally {
            lastChange =
                recording.length > 0
                    ? { at: timelineNow(), animations: recording }
                    : null;
            recording = null;
        }
    };
    const playChange = (records, motion, appMoves) => {
        const yields = (element) => appMoved.has(element) || touches(element, appMoves);
        const gone = [];
        const addedLooks = new Set();
        const addedKeys = new Set();
        const added = new Set();
        for (const record of records)
            for (const node of Array.from(record.addedNodes)) {
                added.add(node);
                if (node instanceof window.Element && node.isConnected) {
                    addedLooks.add(node.outerHTML);
                    const key = keyOf(node);
                    if (key !== null)
                        addedKeys.add(key);
                }
            }
        /** Whether a part of `among` holds the node; the node itself does not count. */
        const heldBy = (node, among) => {
            for (let up = node.parentNode; up; up = up.parentNode)
                if (among.has(up))
                    return true;
            return false;
        };
        // Parts made in this change come in as a whole; their own steps do not count.
        const fresh = (node) => heldBy(node, added);
        const removedLooks = new Set();
        const textBefore = new Map();
        const entering = [];
        /** New parts that took the place of a part of their kind: drawn anew. */
        const redrawn = new Set();
        const kindOf = (node) => `${node.localName} ${node.getAttribute('class') ?? ''}`;
        const moved = new Set();
        const popped = new Set();
        for (const record of records) {
            if (record.type === 'attributes') {
                const element = record.target;
                if (fresh(element))
                    continue;
                const hiddenNow = element.hasAttribute('hidden');
                if (record.oldValue !== null && !hiddenNow)
                    entering.push(element);
                else if (record.oldValue === null &&
                    hiddenNow &&
                    !still(element) &&
                    !yields(element) &&
                    element.parentElement)
                    // The hidden part still holds its place: the copy goes right there.
                    gone.push({
                        parent: element.parentElement,
                        before: element,
                        parts: [element],
                    });
                continue;
            }
            if (record.type === 'characterData') {
                const parent = record.target.parentElement;
                if (!parent || !record.oldValue || fresh(parent))
                    continue;
                if (!textBefore.has(parent))
                    textBefore.set(parent, record.oldValue);
                popped.add(parent);
                continue;
            }
            const onlyText = record.addedNodes.length + record.removedNodes.length > 0 &&
                [
                    ...Array.from(record.addedNodes),
                    ...Array.from(record.removedNodes),
                ].every(node => node.nodeType === 3);
            if (onlyText) {
                const before = Array.from(record.removedNodes)
                    .map(node => node.textContent)
                    .join('');
                const target = record.target;
                if (!before.trim() || !(target instanceof window.Element))
                    continue;
                if (fresh(target))
                    continue;
                if (!textBefore.has(target))
                    textBefore.set(target, before);
                popped.add(target);
                continue;
            }
            const parts = [];
            // A part the App replaces in one step by one of its kind is the same
            // part drawn anew: it neither fades out nor comes in.
            const kinds = (nodes) => new Set(Array.from(nodes)
                .filter(node => node instanceof window.Element)
                .map(node => kindOf(node)));
            const removedKinds = kinds(record.removedNodes);
            const addedKinds = kinds(record.addedNodes);
            for (const node of Array.from(record.removedNodes)) {
                if (!(node instanceof window.Element))
                    continue;
                removedLooks.add(node.outerHTML);
                if (addedKinds.has(kindOf(node)))
                    continue;
                const key = keyOf(node);
                if (key !== null && addedKeys.has(key))
                    continue;
                if (addedLooks.has(node.outerHTML))
                    continue;
                if (node.hasAttribute('hidden') || yields(node))
                    continue;
                parts.push(node);
            }
            const parent = record.target;
            if (parts.length > 0 &&
                parent instanceof window.Element &&
                !still(parent) &&
                !yields(parent))
                gone.push({ parent, before: placeOfGone(record), parts });
            for (const node of Array.from(record.addedNodes)) {
                if (!(node instanceof window.Element) || !node.isConnected)
                    continue;
                if (node.hasAttribute('hidden'))
                    continue;
                const key = keyOf(node);
                if (key !== null && places.has(key))
                    moved.add(node);
                else if (removedKinds.has(kindOf(node))) {
                    redrawn.add(node);
                    entering.push(node);
                }
                else if (!removedLooks.has(node.outerHTML))
                    entering.push(node);
            }
            // Rows beside a change follow it to their new place.
            if (record.target instanceof window.Element)
                for (const sibling of Array.from(record.target.children))
                    if (keyOf(sibling) !== null)
                        moved.add(sibling);
        }
        // Only the outermost new part moves; what it holds comes with it.
        const enteringSet = new Set(entering);
        const comesIn = (element, among) => !heldBy(element, among) &&
            !still(element) &&
            !unseen(element) &&
            !yields(element);
        const outermost = [];
        for (const element of entering) {
            if (!comesIn(element, enteringSet))
                continue;
            // A new part that holds a row shown before is its list drawn anew:
            // those rows go on from where they were, only new rows come in.
            const rows = Array.from(element.querySelectorAll(keyedSelector));
            const known = rows.filter(row => places.has(keyOf(row)));
            if (known.length === 0 && !redrawn.has(element)) {
                outermost.push(element);
                continue;
            }
            for (const row of known)
                moved.add(row);
            const newRows = rows.filter(row => !places.has(keyOf(row)));
            const newSet = new Set(newRows);
            for (const row of newRows)
                if (comesIn(row, newSet))
                    outermost.push(row);
        }
        const outermostSet = new Set(outermost);
        const within = (element) => heldBy(element, outermostSet);
        const starts = new Map();
        for (const element of moved) {
            if (!element.isConnected ||
                still(element) ||
                within(element) ||
                yields(element))
                continue;
            if (unseen(element))
                continue;
            const key = keyOf(element);
            const before = places.get(key);
            if (!before)
                continue;
            const shown = placed.get(key);
            const rest = shown ? shiftWithin(shown) : { x: 0, y: 0 };
            // A row drawn anew takes over what the row before it still played.
            const carried = shown && shown !== element ? shifts.get(shown) : undefined;
            starts.set(element, {
                before,
                x: before.x + rest.x,
                y: before.y + rest.y,
                carried,
            });
        }
        // A part shown where another one went in the same change takes its place:
        // it fades in where it is, so its place is right from the first frame.
        const leftBy = new Set(gone.map(entry => entry.parent));
        fadeOut(gone, motion);
        // Read the new layout before anything of this change plays on it.
        measure();
        const ends = new Map();
        for (const element of starts.keys()) {
            const box = element.getBoundingClientRect();
            const own = shiftOf(element);
            const scrolled = scrolledOf(element);
            ends.set(element, {
                x: box.left + scrolled.x - own.x,
                y: box.top + scrolled.y - own.y,
                box,
            });
        }
        /** A row that is out of the window where it starts and where it ends moves unseen. */
        const unseenMove = (box, dx, dy) => (box.bottom < 0 && box.bottom + dy < 0) ||
            (box.top > window.innerHeight && box.top + dy > window.innerHeight) ||
            (box.right < 0 && box.right + dx < 0) ||
            (box.left > window.innerWidth && box.left + dx > window.innerWidth);
        for (const [element, start] of starts) {
            const now = places.get(keyOf(element));
            const stayed = now !== undefined &&
                Math.abs(now.x - start.before.x) < 1 &&
                Math.abs(now.y - start.before.y) < 1;
            const { carried } = start;
            if (carried) {
                // The new row goes on with the shift of the old one at its time.
                if (carried.kind === 'enter' || stayed) {
                    const next = shift(element, carried.kind, carried.frames, carried.options, carried);
                    next.currentTime = carried.animation.currentTime;
                }
                if (stayed)
                    continue;
            }
            else if (stayed && shifts.has(element))
                continue;
            const end = ends.get(element);
            const dx = start.x - end.x;
            const dy = start.y - end.y;
            if (Math.abs(dx) < 1 && Math.abs(dy) < 1)
                continue;
            if (unseenMove(end.box, dx, dy))
                continue;
            const playing = shifts.get(element);
            if (playing?.kind === 'move')
                playing.animation.cancel();
            shift(element, 'move', [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: motion.base, easing: motion.standard }, { x: dx, y: dy });
        }
        let delay = 0;
        for (const element of outermost) {
            const box = element.getBoundingClientRect();
            if (box.bottom < 0 ||
                box.right < 0 ||
                box.top > window.innerHeight ||
                box.left > window.innerWidth)
                continue;
            const inPlace = element.parentElement !== null && leftBy.has(element.parentElement);
            shift(element, 'enter', inPlace
                ? [{ opacity: 0 }, { opacity: 1 }]
                : [
                    { opacity: 0, transform: `translateY(${motion.distance})` },
                    { opacity: 1, transform: 'none' },
                ], {
                duration: motion.base,
                delay,
                easing: motion.enter,
                fill: 'backwards',
            }, { x: 0, y: inPlace ? 0 : motion.distancePx });
            delay = Math.min(delay + motion.stagger, staggerLimitMs);
        }
        for (const element of popped) {
            if (!element.isConnected || still(element) || within(element))
                continue;
            if (unseen(element) || yields(element))
                continue;
            // The label of a control is no value: a button that names its next
            // step after a press stays as it is.
            if (element.matches(controlSelector))
                continue;
            const now = element.textContent ?? '';
            // A text written again with the same words is no change.
            if (textBefore.get(element) === now || now.trim().length > popLimit)
                continue;
            play(element, [
                { transform: 'scale(1)' },
                { transform: 'scale(1.18)', offset: 0.4 },
                { transform: 'scale(1)' },
            ], { duration: motion.base, easing: motion.spring });
        }
    };
    const observer = new window.MutationObserver(settle);
    const off = new window.AbortController();
    const observe = () => {
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            characterData: true,
            characterDataOldValue: true,
            attributes: true,
            attributeFilter: ['hidden'],
            attributeOldValue: true,
        });
        measure();
    };
    if (document.body)
        observe();
    else
        document.addEventListener('DOMContentLoaded', observe, {
            once: true,
            signal: off.signal,
        });
    const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: off.signal });
    listen(document, 'streamlain:page-leave', () => {
        changingPage = true;
    });
    listen(document, 'streamlain:page-enter', event => {
        // The swap is done; its changes are no changes of the new page.
        observer.takeRecords();
        changingPage = false;
        measure();
        if (event.detail?.initial)
            return;
        const page = document.querySelector('[data-sl-frame-outlet]') ?? document.body;
        if (still(page) || touches(page, appMoving()))
            return;
        const motion = tokens();
        shift(page, 'enter', [
            { opacity: 0, transform: `translateY(${motion.distance})` },
            { opacity: 1, transform: 'none' },
        ], { duration: motion.base, easing: motion.enter }, { x: 0, y: motion.distancePx });
    });
    // A `<details>` opens and closes in height; its summary keeps its own motion.
    const folding = new WeakMap();
    listen(document, 'click', event => {
        if (event.defaultPrevented)
            return;
        const summary = event.target?.closest?.('summary');
        const details = summary?.parentElement;
        if (!summary ||
            !(details instanceof window.HTMLDetailsElement) ||
            details.firstElementChild !== summary ||
            still(details) ||
            touches(details, appMoving()))
            return;
        event.preventDefault();
        const motion = tokens();
        const from = details.getBoundingClientRect().height;
        // It opens or closes at once, as without motion, so the App reads its
        // state and gets its toggle as usual; only the height follows. A second
        // click while it moves turns it around.
        const opening = !details.open;
        folding.get(details)?.cancel();
        details.open = opening;
        const to = opening
            ? details.scrollHeight + (details.offsetHeight - details.clientHeight)
            : summary.getBoundingClientRect().height +
                (details.offsetHeight - details.clientHeight);
        details.style.overflow = 'hidden';
        const fold = play(details, [{ height: `${from}px` }, { height: `${to}px` }], { duration: motion.base, easing: motion.standard });
        folding.set(details, fold);
        void fold.finished
            .catch(() => undefined)
            .then(() => {
            if (folding.get(details) !== fold)
                return;
            folding.delete(details);
            details.style.overflow = '';
        });
    });
    // A pressed button gives way while it is held and is back when let go.
    const pressSelector = 'button, [role="button"], a.button, input[type="submit"], input[type="button"]';
    listen(document, 'pointerdown', event => {
        const pressed = event.target?.closest?.(pressSelector);
        if (!pressed ||
            still(pressed) ||
            pressed.disabled ||
            touches(pressed, appMoving()))
            return;
        const motion = tokens();
        const press = play(pressed, [{ transform: 'scale(0.96)' }], {
            duration: motion.fast,
            easing: motion.standard,
            fill: 'forwards',
        });
        // One press ends once, by letting go or by a cancel: whichever comes
        // first takes the other listener with it, and so does the end of motion.
        const pressing = new window.AbortController();
        off.signal.addEventListener('abort', () => pressing.abort(), {
            signal: pressing.signal,
        });
        // Back at its size at once: the click that follows, and an App that
        // places a menu or a panel at the button, measure the button as it is.
        const release = () => {
            pressing.abort();
            press.cancel();
        };
        window.addEventListener('pointerup', release, { signal: pressing.signal });
        window.addEventListener('pointercancel', release, {
            signal: pressing.signal,
        });
    });
    // A CSS motion of the App starts with an event: it takes over at once.
    listen(document, 'transitionrun', yieldToApp, { capture: true });
    listen(document, 'animationstart', yieldToApp, { capture: true });
    // A new width or a photo that arrives late moves rows without a change.
    listen(window, 'resize', measureSoon);
    listen(document, 'load', measureSoon, { capture: true });
    // A scroll moves only sticky and absolutely placed rows: read them anew
    // once it ends, not at every frame.
    let scrollEnd = 0;
    listen(document, 'scroll', () => {
        window.clearTimeout(scrollEnd);
        scrollEnd = window.setTimeout(measureSoon, scrollSettleMs);
    }, { capture: true, passive: true });
    const installedMotion = {
        dispose() {
            observer.disconnect();
            off.abort();
            layerHost?.remove();
            if (measureFrame)
                window.cancelAnimationFrame(measureFrame);
            if (watchFrame)
                window.cancelAnimationFrame(watchFrame);
            window.clearTimeout(scrollEnd);
            if (host[installed] === installedMotion)
                delete host[installed];
        },
    };
    host[installed] = installedMotion;
    return installedMotion;
}
